import React from 'react';
import { MessageSquare, Clock } from 'lucide-react';
import Modal from "@/components/ui/Modal";
import { BASE_URL } from "@/lib/API";

const apiFileBase = () => String(BASE_URL).replace(/\/api\/?$/, "");

const statusLabel = (u) => {
  if (!u) return '—';
  const map = {
    STARTED: 'Started',
    IN_PROGRESS: 'In progress',
    ON_HOLD: 'On hold',
    WAITING_MATERIAL: 'Waiting for material',
    COMPLETED: 'Completed'
  };
  return map[u] || u;
};

const fmtDateTime = (v) => {
  if (!v) return '--';
  try {
    const d = new Date(v);
    return isNaN(d.getTime()) ? v : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch(e) { return v; }
};

export const TradeWorkUpdatesHistoryModal = ({ isOpen, onClose, updates, trade }) => {
  if (!isOpen || !updates) return null;

  const modalTitle = (
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
        <Clock className="w-5 h-5 text-[#2b448f]" />
      </div>
      <div className="flex flex-col min-w-0">
        <h2 className="text-[16px] font-bold text-slate-800 tracking-tight leading-none">
          Work Update History
        </h2>
        <span className="text-[11px] font-bold text-slate-400 tracking-tight mt-1.5 truncate uppercase">
          {trade} Trade
        </span>
      </div>
    </div>
  );

  const modalFooter = (
    <div className="flex items-center justify-end w-full">
      <button
        type="button"
        onClick={onClose}
        className="px-8 py-2.5 bg-white border border-slate-200 text-slate-500 rounded-md cursor-pointer text-[12px] font-semibold tracking-wider hover:bg-slate-50 transition-all shadow-sm"
      >
        Close
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      footer={modalFooter}
      width="max-w-[600px]"
    >
      <div className="flex flex-col gap-4 font-poppins min-h-[100px] max-h-[60vh] overflow-y-auto pr-2">
        {updates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
             <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center border border-slate-50">
               <MessageSquare className="w-6 h-6 text-slate-200" />
             </div>
             <p className="text-[12px] font-bold text-slate-400 uppercase tracking-widest">No history found</p>
          </div>
        ) : (
          updates.map((u, idx) => (
            <div key={idx} className="rounded-xl border border-slate-100 bg-white p-5 flex flex-col gap-3 shadow-sm hover:border-slate-200 transition-all">
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-1 min-w-0">

                  {u.description ? (
                    <p className="text-[13px] font-bold text-slate-800 leading-relaxed">
                      {u.description}
                    </p>
                  ) : (
                    <span className="text-[13px] font-bold text-slate-800">Status Update</span>
                  )}
                </div>
                <div className="shrink-0 flex flex-col items-end gap-1.5">
                  <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-tight border ${
                    u.status === 'COMPLETED' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-blue-50 text-blue-600 border-blue-100'
                  }`}>
                    {statusLabel(u.status)}
                  </span>
                  {(u.workStartTime || u.workEndTime) && (
                     <div className="text-[10px] font-bold text-slate-500 flex items-center gap-1 bg-slate-50 px-2 py-0.5 rounded border border-slate-100">
                        <Clock className="w-3 h-3 text-slate-400" /> {fmtDateTime(u.workStartTime)} to {fmtDateTime(u.workEndTime)}
                     </div>
                  )}
                  {u.progress != null && (
                     <div className="text-[10px] font-bold text-slate-500">
                        Progress: {u.progress}%
                     </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-slate-50">
                <div className="flex gap-1.5">
                  {(u.images || []).map((img, i) => (
                     <a key={i} href={img.startsWith('http') ? img : `${apiFileBase()}${img}`} target="_blank" rel="noreferrer" className="w-8 h-8 rounded border border-slate-200 overflow-hidden hover:opacity-80 transition-opacity bg-white">
                        <img src={img.startsWith('http') ? img : `${apiFileBase()}${img}`} alt="update" className="w-full h-full object-cover" />
                     </a>
                  ))}
                </div>
                {u.at && (
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter bg-slate-50 px-2 py-0.5 rounded">
                    {new Date(u.at).toLocaleString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit'
                    })}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </Modal>
  );
};
